import { useState } from 'react'
import type { ListingDetail } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { cdnImage } from '../../utils/cdnImage'
import { VariantsEditor } from './VariantsEditor'

interface ListingDetailModalProps {
  listing: ListingDetail
  onClose: () => void
  onApprove: (notes?: string) => Promise<void>
  onReject: (reason: string, notes?: string) => Promise<void>
  onRemove: (reason: string) => Promise<void>
}

export function ListingDetailModal({
  listing,
  onClose,
  onApprove,
  onReject,
  onRemove,
}: ListingDetailModalProps) {
  const [action, setAction] = useState<'approve' | 'reject' | 'remove' | null>(null)
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  const canEdit = usePermission('listings', 'edit')
  const canDelete = usePermission('listings', 'delete')

  const handleAction = async () => {
    setLoading(true)
    try {
      if (action === 'approve') {
        await onApprove(notes || undefined)
      } else if (action === 'reject') {
        await onReject(reason, notes || undefined)
      } else if (action === 'remove') {
        await onRemove(reason)
      }
      onClose()
    } catch {
      // Error handling is done in parent
    } finally {
      setLoading(false)
    }
  }

  const formattedPrice = new Intl.NumberFormat('en-ET', {
    style: 'currency',
    currency: listing.currency,
    minimumFractionDigits: 0,
  }).format(listing.price)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900">Listing Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Images */}
        {listing.images.length > 0 && (
          <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
            {listing.images.map((img, idx) => (
              <img
                key={idx}
                src={cdnImage(img, { w: 256, q: 80 }) ?? img}
                alt={`${listing.title} ${idx + 1}`}
                className="h-32 w-32 flex-shrink-0 rounded-xl object-cover"
              />
            ))}
          </div>
        )}

        {/* Details */}
        <div className="space-y-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{listing.title}</h3>
            <p className="text-2xl font-bold text-primary">{formattedPrice}</p>
          </div>

          <p className="text-gray-600">{listing.description}</p>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Category:</span>
              <span className="ml-2 text-gray-900">{listing.category}</span>
            </div>
            <div>
              <span className="text-gray-500">Condition:</span>
              <span className="ml-2 text-gray-900">{listing.condition || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Location:</span>
              <span className="ml-2 text-gray-900">{listing.location || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500">Status:</span>
              <span className="ml-2 text-gray-900 capitalize">{listing.status}</span>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <p className="text-sm text-gray-500">
              Seller: {listing.seller_name} ({listing.seller_email})
            </p>
            <p className="text-xs text-gray-400">
              Created: {new Date(listing.created_at).toLocaleString()}
            </p>
          </div>

          {listing.moderation_notes && (
            <div className="rounded-xl bg-gray-50 p-4">
              <p className="text-sm font-medium text-gray-700">Moderation Notes</p>
              <p className="text-sm text-gray-600">{listing.moderation_notes}</p>
            </div>
          )}

          {canEdit && (
            <VariantsEditor
              listingId={listing.id}
              defaultPriceCents={Math.round((listing.price ?? 0) * 100)}
            />
          )}
        </div>

        {/* Action Form */}
        {action && (
          <div className="mb-6 rounded-xl border border-gray-200 p-4">
            <h4 className="font-medium text-gray-900 mb-3 capitalize">{action} Listing</h4>
            {(action === 'reject' || action === 'remove') && (
              <CustomTextInput
                label="Reason (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason..."
              />
            )}
            {action !== 'remove' && (
              <CustomTextInput
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
              />
            )}
            <div className="mt-4 flex gap-2">
              <CustomButton
                onClick={handleAction}
                disabled={loading || ((action === 'reject' || action === 'remove') && !reason)}
                variant={action === 'approve' ? 'primary' : 'danger'}
              >
                {loading ? 'Processing...' : `Confirm ${action}`}
              </CustomButton>
              <CustomButton onClick={() => setAction(null)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!action && listing.status === 'pending' && canEdit && (
          <div className="flex gap-2">
            <CustomButton onClick={() => setAction('approve')} variant="primary">
              Approve
            </CustomButton>
            <CustomButton onClick={() => setAction('reject')} variant="danger">
              Reject
            </CustomButton>
            {canDelete && (
              <CustomButton onClick={() => setAction('remove')} variant="secondary">
                Remove
              </CustomButton>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
